import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import type { PlatformClient } from "../../platform/supabase";
import { credentialStatusLabel } from "../../platform/locale";

type Overview = {
  learner: {
    learnerId:string;
    displayName:string;
    status:string;
    gradeLevel?:string|null;
    classLabel?:string|null;
    schoolName?:string|null;
  };
  journey?: {
    journeyId:string;
    status:string;
    startedAt:string;
    closedAt?:string|null;
  } | null;
  counts: {
    experiences:number;
    evaluations:number;
    achievements:number;
    credentials:number;
  };
  recentExperiences:Array<{
    participationId:string;
    activityTitle:string;
    programTitle:string;
    status:string;
    completedAt?:string|null;
    attendedAt?:string|null;
    locationName?:string|null;
  }>;
  recentCredentials:Array<{
    credentialId:string;
    credentialName:string;
    achievementName:string;
    issuerName:string;
    status:string;
    isValid:boolean;
    issuedAt:string;
    expiresAt?:string|null;
    verificationUri?:string|null;
  }>;
};

type ExperienceItem = {
  participationId:string;
  status:string;
  programTitle:string;
  programType:string;
  activityTitle:string;
  activityType:string;
  deliveryMode:string;
  attendedAt?:string|null;
  completedAt?:string|null;
  sessionStartsAt?:string|null;
  sessionEndsAt?:string|null;
  locationName?:string|null;
  evidence:Array<{
    evidenceId:string;
    title:string;
    type:string;
    status:string;
    verifiedAt?:string|null;
  }>;
};

type Growth = {
  evaluations:Array<{
    evaluationResultId:string;
    name:string;
    numericScore?:number|null;
    normalizedScore?:number|null;
    observedAt:string;
    confirmedAt?:string|null;
  }>;
  competencies:Array<{
    competencyResultId:string;
    competencyId:string;
    competencyName:string;
    score:number;
    observedAt:string;
  }>;
  achievements:Array<{
    achievementId:string;
    name:string;
    description?:string|null;
    periodKey:string;
    confirmedAt?:string|null;
  }>;
};

type CredentialRow = {
  credential_id:string;
  external_credential_id:string;
  status:string;
  issued_at:string;
  expires_at?:string|null;
  verification_uri?:string|null;
  credential_name:string;
  credential_description?:string|null;
  image_uri?:string|null;
  achievement_name:string;
  issuer_organization_id:string;
  issuer_name:string;
};

type CredentialDetail = {
  credentialId:string;
  credentialName:string;
  credentialDescription?:string|null;
  imageUri?:string|null;
  achievementName:string;
  achievementDescription?:string|null;
  issuerName:string;
  externalProfile:string;
  externalCredentialId:string;
  status:string;
  isValid:boolean;
  issuedAt:string;
  expiresAt?:string|null;
  verificationUri?:string|null;
};

type ShareLink = {
  shareId:string;
  label:string;
  status:string;
  includeExperiences:boolean;
  includeGrowth:boolean;
  includeCredentials:boolean;
  shareUrl:string;
  expiresAt:string;
  createdAt:string;
};

type PublicShare = {
  shareId:string;
  label:string;
  expiresAt:string;
  profile:{
    displayName:string;
    schoolName?:string|null;
  };
  experiences:Array<{
    programTitle:string;
    activityTitle:string;
    activityType:string;
    completedAt?:string|null;
    attendedAt?:string|null;
  }>;
  growth:{
    competencies:Array<{
      name:string;
      score:number;
      observedAt:string;
    }>;
    achievements:Array<{
      name:string;
      description?:string|null;
      periodKey:string;
      confirmedAt?:string|null;
    }>;
  };
  credentials:Array<{
    credentialName:string;
    achievementName:string;
    issuerName:string;
    status:string;
    isValid:boolean;
    issuedAt:string;
    expiresAt?:string|null;
    verificationUri?:string|null;
  }>;
};

type PublicPortfolioShare = {
  shareId:string;
  label:string;
  expiresAt:string;
  documentType:string;
  title:string;
  versionNo:number;
  content:{
    sections?:Array<{
      key:string;
      title:string;
      body:string;
      visible:boolean;
      sourceIds?:string[];
    }>;
    portfolioMeta?:{
      purpose?:string;
      target?:string;
      headline?:string;
    };
  };
  profile:{
    displayName:string;
    schoolName?:string|null;
  };
  lifeStamps:Array<{
    credentialName:string;
    achievementName:string;
    issuerName:string;
    status:string;
    isValid:boolean;
    issuedAt:string;
    expiresAt?:string|null;
    verificationUri?:string|null;
    trustLabel?:string|null;
  }>;
  experiences:Array<{
    programTitle:string;
    activityTitle:string;
    activityType:string;
    status:string;
    attendedAt?:string|null;
    completedAt?:string|null;
    trustLabel?:string|null;
  }>;
};

function portfolioDocumentTypeText(value:string){
  if(value==="SELF_INTRODUCTION") return "자기소개서";
  if(value==="ACTIVITY_RECORD") return "활동기술서";
  if(value==="CAREER_PORTFOLIO") return "진로성장 포트폴리오";
  if(value==="PROJECT_PORTFOLIO") return "프로젝트 포트폴리오";
  if(value==="GROWTH_REPORT") return "성장 리포트";
  return "포트폴리오";
}

function portfolioPurposeText(value?:string){
  if(value==="SCHOOL_RECORD") return "학교 활동 정리";
  if(value==="MAJOR_EXPLORATION") return "대학·학과 탐색";
  if(value==="PROJECT_SHOWCASE") return "프로젝트 소개";
  if(value==="INTERVIEW") return "면접 준비";
  if(value==="PERSONAL_GROWTH") return "성장 기록";
  return "포트폴리오";
}

function formatDate(value?:string|null){
  if(!value) return "-";
  return new Date(value).toLocaleDateString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit"
  });
}

function statusText(status:string,isValid?:boolean,expiresAt?:string|null){
  if(isValid) return "유효";
  if(status==="REVOKED") return "취소";
  if(status==="EXPIRED") return "만료";
  if(status==="ACTIVE" && expiresAt && new Date(expiresAt).getTime()<=Date.now()) return "만료";
  if(status==="ACTIVE") return "활성";
  if(status==="COMPLETED") return "완료";
  if(status==="ATTENDED") return "참석";
  if(status==="CONFIRMED") return "확정";
  return status;
}

async function copyText(value:string){
  await navigator.clipboard.writeText(value);
}

export function WalletClaimPage({client}:{client:PlatformClient}){
  const [token,setToken]=useState(
    ()=>new URLSearchParams(window.location.search).get("token")
      || window.sessionStorage.getItem("rs-career-wallet-claim-token")
      || ""
  );
  const [busy,setBusy]=useState(false);
  const [message,setMessage]=useState("");

  async function claim(){
    if(!token.trim()) return;
    setBusy(true);
    setMessage("");

    const {data,error}=await client.rpc("claim_learner_wallet",{
      p_token:token.trim()
    });

    if(error){
      setMessage(error.message);
      setBusy(false);
      return;
    }

    if((data as {status?:string})?.status==="CLAIMED"){
      window.sessionStorage.removeItem("rs-career-wallet-claim-token");
      window.location.replace("/");
      return;
    }

    setMessage("Wallet 연결 결과를 확인할 수 없습니다.");
    setBusy(false);
  }

  return (
    <section className="wallet-claim-card">
      <p className="eyebrow">Wallet 연결</p>
      <h2>학교의 학생 기록과 내 계정을 연결합니다</h2>
      <p>
        학교에서 받은 1회성 연결 링크 또는 코드를 사용하세요.
        이름이나 이메일이 비슷하다는 이유만으로 자동 연결하지 않습니다.
      </p>
      <label>
        연결 코드
        <input
          value={token}
          onChange={(event)=>setToken(event.target.value)}
          placeholder="64자리 Wallet 연결 코드"
          autoComplete="off"
        />
      </label>
      <button className="primary" disabled={busy || !token.trim()} onClick={claim}>
        {busy ? "연결 중…" : "내 학생 기록 연결"}
      </button>
      {message && <p className="error">{message}</p>}
    </section>
  );
}


export function WalletBadgeEmailClaimPage({
  client,
  token
}:{
  client:PlatformClient;
  token:string;
}){
  const [busy,setBusy]=useState(false);
  const [message,setMessage]=useState("");

  async function claim(){
    if(!token)return;
    setBusy(true);
    setMessage("");

    const {data,error}=await client.rpc("claim_learner_wallet_from_badge_email",{
      p_token:token
    });

    if(error){
      const raw=error.message || "";
      if(raw.includes("CLAIM_EMAIL_MISMATCH")){
        setMessage("Life Stamp 안내를 받은 이메일의 Google 계정으로 로그인해 주세요.");
      }else if(raw.includes("BADGE_EMAIL_CLAIM_EXPIRED")){
        setMessage("수령 링크가 만료되었습니다. 학교 담당자에게 새 안내를 요청해 주세요.");
      }else{
        setMessage(raw);
      }
      setBusy(false);
      return;
    }

    if((data as {status?:string})?.status==="CLAIMED"){
      window.sessionStorage.removeItem("rs-career-badge-email-claim-token");
      window.location.replace("/credentials");
      return;
    }

    setMessage("Life Stamp 연결 결과를 확인할 수 없습니다.");
    setBusy(false);
  }

  return (
    <section className="wallet-claim-card badge-email-claim-card">
      <p className="eyebrow">Life Stamp 수령</p>
      <h2>내 Life Passport에 연결</h2>
      <p>
        이메일로 받은 Life Stamp를 현재 로그인한 Google 계정과 연결합니다.
        안내를 받은 이메일과 로그인 이메일이 같아야 합니다.
      </p>
      <div className="claim-trust-note">
        <strong>수령 확인</strong>
        <span>이 링크만으로 소유권이 확정되지 않습니다. 로그인 이메일과 발급 대상 학생을 다시 확인합니다.</span>
      </div>
      <button className="primary" disabled={busy || !token} onClick={claim}>
        {busy ? "연결 확인 중…" : "Life Stamp 연결하기"}
      </button>
      {message && <p className="error">{message}</p>}
    </section>
  );
}

export function WalletJourneyPage({client}:{client:PlatformClient}){
  const [data,setData]=useState<Overview|null>(null);
  const [error,setError]=useState("");

  useEffect(()=>{
    let active=true;
    client.rpc("wallet_overview").then(({data,error})=>{
      if(!active) return;
      if(error) setError(error.message);
      else setData(data as Overview);
    });
    return()=>{active=false;};
  },[client]);

  if(error) return <p className="error">{error}</p>;
  if(!data) return <p className="muted">나의 진로성장을 불러오는 중…</p>;

  return (
    <section className="wallet-page">
      <div className="wallet-hero">
        <div>
          <p className="eyebrow">나의 진로성장</p>
          <h2>{data.learner.displayName}</h2>
          <p>
            {data.learner.schoolName || "학교 미확인"}
            {data.learner.gradeLevel ? ` · ${data.learner.gradeLevel}학년` : ""}
            {data.learner.classLabel ? ` · ${data.learner.classLabel}반` : ""}
          </p>
        </div>
        <span className="status-pill good">{data.journey?.status==="ACTIVE" ? "성장 여정 진행 중" : "여정 확인"}</span>
      </div>

      <div className="wallet-metric-grid">
        <WalletMetric label="경험" value={data.counts.experiences} />
        <WalletMetric label="평가" value={data.counts.evaluations} />
        <WalletMetric label="성취" value={data.counts.achievements} />
        <WalletMetric label="자격증명" value={data.counts.credentials} />
      </div>

      <div className="wallet-two-column">
        <WalletSection title="최근 경험">
          {data.recentExperiences.length ? data.recentExperiences.map((item)=>(
            <div className="wallet-list-row" key={item.participationId}>
              <div>
                <strong>{item.activityTitle}</strong>
                <span>{item.programTitle}</span>
                <small>{formatDate(item.completedAt || item.attendedAt)}{item.locationName ? ` · ${item.locationName}` : ""}</small>
              </div>
              <span className="status-pill">{statusText(item.status)}</span>
            </div>
          )):<WalletEmpty text="아직 완료된 경험이 없습니다." />}
        </WalletSection>

        <WalletSection title="최근 자격증명">
          {data.recentCredentials.length ? data.recentCredentials.map((item)=>(
            <div className="wallet-list-row" key={item.credentialId}>
              <div>
                <strong>{item.credentialName}</strong>
                <span>{item.achievementName}</span>
                <small>{item.issuerName} · {formatDate(item.issuedAt)}</small>
              </div>
              <span className={item.isValid ? "status-pill good" : "status-pill"}>
                {statusText(item.status,item.isValid,item.expiresAt)}
              </span>
            </div>
          )):<WalletEmpty text="아직 발급된 자격증명이 없습니다." />}
        </WalletSection>
      </div>
    </section>
  );
}

export function WalletExperiencesPage({client}:{client:PlatformClient}){
  const [items,setItems]=useState<ExperienceItem[]>([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState("");

  useEffect(()=>{
    let active=true;
    client.rpc("wallet_experiences").then(({data,error})=>{
      if(!active) return;
      if(error) setError(error.message);
      else setItems((data || []) as ExperienceItem[]);
      setLoading(false);
    });
    return()=>{active=false;};
  },[client]);

  return (
    <section className="wallet-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">경험 기록</p>
          <h2>나의 경험</h2>
          <p className="route-description">참여가 확인된 프로그램과 검증된 활동 증빙만 보여줍니다.</p>
        </div>
        <span className="phase-badge">BUILD-05</span>
      </div>
      {error && <p className="error">{error}</p>}
      {loading ? <p className="muted">경험을 불러오는 중…</p> : (
        <div className="wallet-timeline">
          {items.length ? items.map((item)=>(
            <article className="wallet-experience-card" key={item.participationId}>
              <div className="wallet-experience-head">
                <div>
                  <p className="eyebrow">{item.programTitle}</p>
                  <h3>{item.activityTitle}</h3>
                </div>
                <span className="status-pill good">{statusText(item.status)}</span>
              </div>
              <p className="muted">
                {formatDate(item.completedAt || item.attendedAt)}
                {item.locationName ? ` · ${item.locationName}` : ""}
                {item.deliveryMode ? ` · ${item.deliveryMode}` : ""}
              </p>
              {item.evidence.length>0 && (
                <div className="wallet-evidence-list">
                  {item.evidence.map((evidence)=>(
                    <span key={evidence.evidenceId}>✓ {evidence.title}</span>
                  ))}
                </div>
              )}
            </article>
          )):<WalletEmpty text="표시할 경험이 없습니다." />}
        </div>
      )}
    </section>
  );
}

export function WalletGrowthPage({client}:{client:PlatformClient}){
  const [data,setData]=useState<Growth|null>(null);
  const [error,setError]=useState("");

  useEffect(()=>{
    let active=true;
    client.rpc("wallet_growth").then(({data,error})=>{
      if(!active) return;
      if(error) setError(error.message);
      else setData(data as Growth);
    });
    return()=>{active=false;};
  },[client]);

  if(error) return <p className="error">{error}</p>;
  if(!data) return <p className="muted">성장 기록을 불러오는 중…</p>;

  return (
    <section className="wallet-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">성장 기록</p>
          <h2>나의 성장</h2>
          <p className="route-description">확정된 평가·역량·성취만 시간순으로 보여줍니다.</p>
        </div>
        <span className="phase-badge">BUILD-05</span>
      </div>

      <div className="wallet-two-column">
        <WalletSection title="평가 결과">
          {data.evaluations.length ? data.evaluations.map((item)=>(
            <div className="wallet-list-row" key={item.evaluationResultId}>
              <div>
                <strong>{item.name}</strong>
                <span>{item.normalizedScore ?? item.numericScore ?? "-"}점</span>
                <small>{formatDate(item.confirmedAt || item.observedAt)}</small>
              </div>
              <span className="status-pill good">확정</span>
            </div>
          )):<WalletEmpty text="확정된 평가 결과가 없습니다." />}
        </WalletSection>

        <WalletSection title="역량">
          {data.competencies.length ? data.competencies.map((item)=>(
            <div className="wallet-list-row" key={item.competencyResultId}>
              <div>
                <strong>{item.competencyName}</strong>
                <small>{formatDate(item.observedAt)}</small>
              </div>
              <span className="wallet-score">{item.score}</span>
            </div>
          )):<WalletEmpty text="아직 역량 결과가 없습니다." />}
        </WalletSection>
      </div>

      <WalletSection title="확인된 성취">
        {data.achievements.length ? data.achievements.map((item)=>(
          <div className="wallet-list-row" key={item.achievementId}>
            <div>
              <strong>{item.name}</strong>
              <span>{item.description || item.periodKey}</span>
              <small>{formatDate(item.confirmedAt)}</small>
            </div>
            <span className="status-pill good">성취</span>
          </div>
        )):<WalletEmpty text="확인된 성취가 없습니다." />}
      </WalletSection>
    </section>
  );
}


type WalletWritingKind="SELF_INTRO"|"PORTFOLIO";

function joinWalletNames(values:string[]){
  const names=Array.from(new Set(values.filter(Boolean)));
  if(names.length<=3) return names.join(", ");
  return names.slice(0,3).join(", ")+" 외 "+(names.length-3)+"개";
}

function composeWalletBadgeDraft(
  kind:WalletWritingKind,
  goal:string,
  experience:string,
  nextStep:string,
  badges:CredentialDetail[]
){
  const badgeNames=joinWalletNames(badges.map((item)=>item.achievementName||item.credentialName));
  const records=badges.map((item,index)=>{
    const description=item.achievementDescription||item.credentialDescription;
    const lines=[
      "### "+(item.achievementName||item.credentialName),
      "- 배지명: "+item.credentialName,
      "- 발급기관: "+item.issuerName,
      "- 발급일: "+formatDate(item.issuedAt),
      "- 현재 상태: "+statusText(item.status,item.isValid,item.expiresAt)
    ];
    if(description) lines.push("- 발급 설명: "+description);
    if(item.verificationUri) lines.push("- 검증 링크: "+item.verificationUri);
    return (index+1)+". "+lines.join("\n");
  }).join("\n\n");

  if(kind==="SELF_INTRO"){
    return [
      "# 자기소개서 초안",
      "",
      "관심 분야·진로 목표: "+(goal.trim()||"[관심 분야 또는 지원 목표를 입력하세요]"),
      "",
      "저는 "+(goal.trim()||"[관심 분야 또는 지원 분야]")+"에 관심을 두고 있습니다.",
      "제 지갑에는 "+badges.length+"개의 배지 기록이 담겨 있습니다. "+badgeNames+"에 대한 발급 기록을 근거로 제 경험을 소개합니다.",
      experience.trim()
        ? "그 활동에서 제가 실제로 한 일과 배운 점은 다음과 같습니다. "+experience.trim()
        : "[배지 활동에서 실제로 맡은 역할, 한 일, 결과와 배운 점을 적어 주세요.]",
      "앞으로 해보고 싶은 일: "+(nextStep.trim()||"[다음에 도전하고 싶은 일이나 목표를 입력하세요]"),
      "",
      "## 배지 근거",
      records,
      "",
      "※ 배지의 발급명·기관·일자·상태를 근거로 구성한 초안입니다. 본인의 실제 경험과 계획에 맞게 문장을 다듬어 주세요."
    ].join("\n");
  }

  return [
    "# 포트폴리오 초안",
    "",
    "## 진로 방향",
    goal.trim()||"[관심 분야 또는 진로 목표를 입력하세요]",
    "",
    "## 배지로 확인되는 기록",
    records,
    "",
    "## 나의 경험과 배움",
    experience.trim()||"[배지 활동에서 실제로 맡은 역할, 한 일, 결과와 배운 점을 적어 주세요.]",
    "",
    "## 다음 계획",
    nextStep.trim()||"[앞으로 더 해보고 싶은 일이나 다음 목표를 입력하세요]",
    "",
    "※ 발급기관이 기록한 배지 정보와 본인이 입력한 경험을 구분해 담았습니다. 실제 사실에 맞게 최종 확인해 주세요."
  ].join("\n");
}

function WalletBadgeWritingStudio({client,items}:{client:PlatformClient;items:CredentialRow[]}){
  const [kind,setKind]=useState<WalletWritingKind>("SELF_INTRO");
  const [draftKind,setDraftKind]=useState<WalletWritingKind>("SELF_INTRO");
  const [selectedIds,setSelectedIds]=useState<string[]>([]);
  const [goal,setGoal]=useState("");
  const [experience,setExperience]=useState("");
  const [nextStep,setNextStep]=useState("");
  const [draft,setDraft]=useState("");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [feedback,setFeedback]=useState("");

  useEffect(()=>{
    setSelectedIds(items
      .filter((item)=>(item.status||"").toUpperCase()==="ACTIVE")
      .map((item)=>item.credential_id));
  },[items]);

  async function generate(){
    const selected=items.filter((item)=>selectedIds.includes(item.credential_id));
    if(!selected.length){
      setError("문서에 넣을 배지를 하나 이상 선택하세요.");
      return;
    }

    setBusy(true);
    setError("");
    setFeedback("");
    try{
      const details=await Promise.all(selected.map(async(item)=>{
        const {data,error:rpcError}=await client.rpc("wallet_credential_detail",{
          p_credential_id:item.credential_id
        });
        if(rpcError) throw new Error(item.credential_name+": "+rpcError.message);
        const detail=Array.isArray(data)?data[0]:data;
        if(!detail) throw new Error(item.credential_name+"의 상세 정보를 불러오지 못했습니다.");
        return detail as CredentialDetail;
      }));
      setDraft(composeWalletBadgeDraft(kind,goal,experience,nextStep,details));
      setDraftKind(kind);
      setFeedback("배지 기록을 반영해 초안을 만들었습니다. 내용을 확인하고 자유롭게 고쳐 쓰세요.");
    }catch(reason){
      setError(reason instanceof Error?reason.message:"배지 정보를 불러오지 못했습니다.");
    }finally{
      setBusy(false);
    }
  }

  async function copyDraft(){
    if(!draft) return;
    try{
      await navigator.clipboard.writeText(draft);
      setFeedback("초안을 복사했습니다.");
      setError("");
    }catch{
      setError("복사 권한을 확인한 뒤 다시 시도하세요.");
    }
  }

  function downloadDraft(){
    if(!draft) return;
    const fileName=draftKind==="SELF_INTRO"?"자기소개서-초안.txt":"포트폴리오-초안.txt";
    const url=URL.createObjectURL(new Blob([draft],{type:"text/plain;charset=utf-8"}));
    const link=document.createElement("a");
    link.href=url;
    link.download=fileName;
    link.click();
    window.setTimeout(()=>URL.revokeObjectURL(url),1000);
    setFeedback("텍스트 파일로 저장했습니다.");
    setError("");
  }

  return (
    <div className="wallet-writing-studio">
      <div className="wallet-writing-intro">
        <div>
          <p className="eyebrow">Badge-based writing</p>
          <h3>배지로 자기소개서·포트폴리오 초안 만들기</h3>
          <p>발급명, 성취명, 기관, 발급일과 검증 링크를 근거로 정리합니다. 실제 역할과 배운 점은 직접 입력한 내용만 반영합니다.</p>
        </div>
        <span className="wallet-writing-count">{items.length}개 배지</span>
      </div>

      <div className="wallet-writing-layout">
        <section className="wallet-writing-panel">
          <div className="wallet-writing-panel-head">
            <div>
              <p className="eyebrow">01 · 초안 설정</p>
              <h3>무엇을 만들까요?</h3>
            </div>
          </div>

          <div className="wallet-writing-type-toggle" role="group" aria-label="작성할 문서 종류">
            <button type="button" aria-pressed={kind==="SELF_INTRO"} onClick={()=>setKind("SELF_INTRO")}>자기소개서</button>
            <button type="button" aria-pressed={kind==="PORTFOLIO"} onClick={()=>setKind("PORTFOLIO")}>포트폴리오</button>
          </div>

          <div className="wallet-writing-fields">
            <label className="wallet-writing-field">
              <span>관심 분야·진로 목표</span>
              <input value={goal} maxLength={120} onChange={(event)=>setGoal(event.target.value)} placeholder="예: 생명과학 연구, 영상 제작, 간호학과 진학" />
            </label>
            <label className="wallet-writing-field">
              <span>배지 활동에서 내가 실제로 한 일과 배운 점</span>
              <textarea value={experience} maxLength={1200} onChange={(event)=>setExperience(event.target.value)} placeholder="맡은 역할, 직접 한 행동, 결과, 어려웠던 점과 배운 점을 적어 주세요." />
            </label>
            <label className="wallet-writing-field">
              <span>앞으로 해보고 싶은 일</span>
              <input value={nextStep} maxLength={300} onChange={(event)=>setNextStep(event.target.value)} placeholder="예: 다음에는 팀 프로젝트에서 실험 결과를 발표하고 싶습니다." />
            </label>
          </div>

          <fieldset className="wallet-writing-badge-list">
            <legend>근거로 사용할 배지 <span>{selectedIds.length}개 선택</span></legend>
            <div className="wallet-writing-badge-toolbar">
              <button type="button" className="secondary compact-button" onClick={()=>setSelectedIds(items.map((item)=>item.credential_id))}>전체 선택</button>
              <button type="button" className="secondary compact-button" onClick={()=>setSelectedIds([])}>선택 해제</button>
            </div>
            {items.length ? (
              <div className="wallet-writing-badge-items">
                {items.map((item)=>(
                  <label className="wallet-writing-badge-option" key={item.credential_id}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.credential_id)}
                      onChange={()=>{
                        setSelectedIds((current)=>current.includes(item.credential_id)
                          ?current.filter((id)=>id!==item.credential_id)
                          :[...current,item.credential_id]);
                      }}
                    />
                    <span>
                      <strong>{item.credential_name}</strong>
                      <small>{item.achievement_name} · {item.issuer_name}</small>
                      <small>{formatDate(item.issued_at)} · {statusText(item.status)}</small>
                    </span>
                  </label>
                ))}
              </div>
            ):<WalletEmpty text="지갑에 배지가 없습니다." />}
          </fieldset>

          {error && <p className="error">{error}</p>}
          <button type="button" className="primary wallet-writing-create" disabled={busy||selectedIds.length===0} onClick={generate}>
            {busy?"배지 정보를 정리하는 중…":kind==="SELF_INTRO"?"자기소개서 초안 만들기":"포트폴리오 초안 만들기"}
          </button>
          <p className="wallet-writing-footnote">만료·취소된 기록을 선택하면 초안에 현재 상태가 함께 표시됩니다. 배지 정보와 본인이 적은 경험을 확인한 뒤 제출하세요.</p>
        </section>

        <section className="wallet-writing-panel wallet-writing-result">
          <div className="wallet-writing-result-head">
            <div>
              <p className="eyebrow">02 · 편집 가능한 결과</p>
              <h3>작성 초안</h3>
            </div>
            <div className="wallet-button-row">
              <button type="button" className="secondary compact-button" disabled={!draft} onClick={copyDraft}>복사</button>
              <button type="button" className="primary compact-button" disabled={!draft} onClick={downloadDraft}>TXT 저장</button>
            </div>
          </div>
          {feedback && <p className="wallet-writing-feedback">{feedback}</p>}
          <textarea
            className="wallet-writing-output"
            aria-label="생성된 자기소개서 또는 포트폴리오 초안"
            value={draft}
            onChange={(event)=>setDraft(event.target.value)}
            placeholder="배지와 작성 정보를 선택한 뒤 초안을 만들면 이곳에서 바로 수정할 수 있습니다."
          />
        </section>
      </div>
    </div>
  );
}

export function WalletCredentialsPage({client,onUseInPortfolio}:{client:PlatformClient;onUseInPortfolio?:(credentialId:string)=>void}){
  const [items,setItems]=useState<CredentialRow[]>([]);
  const [selectedId,setSelectedId]=useState("");
  const [detail,setDetail]=useState<CredentialDetail|null>(null);
  const [filter,setFilter]=useState<"ALL"|"VALID"|"ARCHIVE">("ALL");
  const [query,setQuery]=useState("");
  const [detailLoading,setDetailLoading]=useState(false);
  const [error,setError]=useState("");
  const [notice,setNotice]=useState("");

  useEffect(()=>{
    let active=true;
    client.rpc("wallet_credentials").then(({data,error})=>{
      if(!active) return;
      if(error) setError(error.message);
      else{
        const rows=(data || []) as CredentialRow[];
        setItems(rows);
        setSelectedId((current)=>rows.some((row)=>row.credential_id===current) ? current : rows[0]?.credential_id || "");
      }
    });
    return()=>{active=false;};
  },[client]);

  useEffect(()=>{
    if(!selectedId){
      setDetail(null);
      return;
    }
    let active=true;
    setDetailLoading(true);
    setNotice("");
    client.rpc("wallet_credential_detail",{p_credential_id:selectedId}).then(({data,error})=>{
      if(!active) return;
      setDetailLoading(false);
      if(error) setError(error.message);
      else setDetail(data as CredentialDetail);
    });
    return()=>{active=false;};
  },[client,selectedId]);

  const rowIsValid=(item:CredentialRow)=>{
    if(item.status!=="ACTIVE") return false;
    if(!item.expires_at) return true;
    return new Date(item.expires_at).getTime()>Date.now();
  };

  const validCount=useMemo(()=>items.filter(rowIsValid).length,[items]);
  const issuerCount=useMemo(()=>new Set(items.map((item)=>item.issuer_name)).size,[items]);
  const filteredItems=useMemo(()=>{
    const normalized=query.trim().toLowerCase();
    return items.filter((item)=>{
      const valid=rowIsValid(item);
      if(filter==="VALID" && !valid) return false;
      if(filter==="ARCHIVE" && valid) return false;
      if(!normalized) return true;
      return [
        item.credential_name,
        item.achievement_name,
        item.issuer_name,
        item.external_credential_id
      ].some((value)=>value?.toLowerCase().includes(normalized));
    });
  },[items,filter,query]);

  const selectedRow=useMemo(
    ()=>items.find((item)=>item.credential_id===selectedId) || null,
    [items,selectedId]
  );

  async function shareCredential(){
    if(!detail?.verificationUri) return;
    setNotice("");
    try{
      if("share" in navigator){
        await navigator.share({
          title:detail.credentialName,
          text:`${detail.issuerName} · ${detail.achievementName}`,
          url:detail.verificationUri
        });
        return;
      }
      await copyText(detail.verificationUri);
      setNotice("검증 링크를 복사했습니다.");
    }catch(error){
      if(error instanceof DOMException && error.name==="AbortError") return;
      try{
        await copyText(detail.verificationUri);
        setNotice("검증 링크를 복사했습니다.");
      }catch{
        setNotice("공유 링크를 복사하지 못했습니다.");
      }
    }
  }

  return (
    <section className="wallet-page life-stamp-page">
      <div className="life-stamp-heading">
        <div>
          <p className="eyebrow">LIFE STAMP VAULT</p>
          <h2>나의 Life Stamp</h2>
          <p>활동과 성취를 확인해 발급된 디지털배지를 한곳에서 보관하고 검증·공유합니다.</p>
        </div>
        <span className="life-stamp-standard">Open Badges 3.0</span>
      </div>

      <div className="life-stamp-summary">
        <article><span>보유</span><strong>{items.length}</strong><small>전체 Life Stamp</small></article>
        <article><span>유효</span><strong>{validCount}</strong><small>현재 검증 가능</small></article>
        <article><span>발급기관</span><strong>{issuerCount}</strong><small>확인된 Issuer</small></article>
      </div>

      <div className="life-stamp-toolbar">
        <label className="life-stamp-search">
          <span>⌕</span>
          <input
            value={query}
            onChange={(event)=>setQuery(event.target.value)}
            placeholder="배지명, 성취, 발급기관 검색"
          />
        </label>
        <div className="life-stamp-filters">
          {[
            ["ALL","전체"],
            ["VALID","유효"],
            ["ARCHIVE","취소·만료"]
          ].map(([value,label])=>(
            <button
              key={value}
              className={filter===value ? "active" : ""}
              onClick={()=>setFilter(value as "ALL"|"VALID"|"ARCHIVE")}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="life-stamp-vault-layout">
        <div className="life-stamp-gallery">
          {filteredItems.length ? filteredItems.map((item)=>{
            const valid=rowIsValid(item);
            return (
              <button
                type="button"
                key={item.credential_id}
                className={selectedId===item.credential_id ? "life-stamp-card active" : "life-stamp-card"}
                onClick={()=>setSelectedId(item.credential_id)}
              >
                <div className="life-stamp-card-art">
                  {item.image_uri ? (
                    <img src={item.image_uri} alt="" loading="lazy" />
                  ) : (
                    <div className="life-stamp-fallback">
                      <span>RS</span>
                      <strong>LIFE<br/>STAMP</strong>
                    </div>
                  )}
                  <span className={valid ? "life-stamp-state valid" : "life-stamp-state"}>
                    {statusText(item.status,valid,item.expires_at)}
                  </span>
                </div>
                <div className="life-stamp-card-copy">
                  <p>{item.issuer_name}</p>
                  <h3>{item.credential_name}</h3>
                  <strong>{item.achievement_name}</strong>
                  <small>발급 {formatDate(item.issued_at)}</small>
                </div>
              </button>
            );
          }):<div className="life-stamp-empty"><WalletEmpty text={items.length ? "검색 조건에 맞는 Life Stamp가 없습니다." : "아직 발급된 Life Stamp가 없습니다."} /></div>}
        </div>

        <aside className="life-stamp-detail">
          {detailLoading ? (
            <p className="muted">Life Stamp를 확인하는 중…</p>
          ) : detail ? (
            <>
              <div className="life-stamp-detail-visual">
                <div className="life-stamp-large-art">
                  {(detail.imageUri || selectedRow?.image_uri) ? (
                    <img src={detail.imageUri || selectedRow?.image_uri || ""} alt="" />
                  ) : (
                    <div className="life-stamp-fallback large">
                      <span>RS</span>
                      <strong>LIFE<br/>STAMP</strong>
                    </div>
                  )}
                </div>
                <span className={detail.isValid ? "life-stamp-state valid" : "life-stamp-state"}>
                  {statusText(detail.status,detail.isValid,detail.expiresAt)}
                </span>
              </div>

              <div className="life-stamp-detail-copy">
                <p className="eyebrow">{detail.issuerName}</p>
                <h2>{detail.credentialName}</h2>
                <h3>{detail.achievementName}</h3>
                {detail.credentialDescription && <p>{detail.credentialDescription}</p>}
              </div>

              {onUseInPortfolio && (
                <div className="life-stamp-actions portfolio-life-stamp-action">
                  <button className="secondary" type="button" onClick={()=>onUseInPortfolio(detail.credentialId)}>문서에 활용</button>
                </div>
              )}

              <div className="life-stamp-proof-grid">
                <div><span>발급기관</span><strong>{detail.issuerName}</strong></div>
                <div><span>발급일</span><strong>{formatDate(detail.issuedAt)}</strong></div>
                <div><span>만료일</span><strong>{detail.expiresAt ? formatDate(detail.expiresAt) : "만료 없음"}</strong></div>
                <div><span>표준</span><strong>{detail.externalProfile || "Open Badges 3.0"}</strong></div>
              </div>

              {detail.achievementDescription && (
                <section className="life-stamp-achievement">
                  <span>확인된 활동·성취</span>
                  <strong>{detail.achievementName}</strong>
                  <p>{detail.achievementDescription}</p>
                </section>
              )}

              <section className="life-stamp-credential-id">
                <span>Credential ID</span>
                <code>{detail.externalCredentialId}</code>
                <button
                  type="button"
                  onClick={async()=>{
                    await copyText(detail.externalCredentialId);
                    setNotice("Credential ID를 복사했습니다.");
                  }}
                >
                  ID 복사
                </button>
              </section>

              {detail.verificationUri && (
                <>
                  <div className="life-stamp-actions">
                    <button className="primary" type="button" onClick={shareCredential}>공유하기</button>
                    <button
                      className="secondary"
                      type="button"
                      onClick={async()=>{
                        await copyText(detail.verificationUri!);
                        setNotice("검증 링크를 복사했습니다.");
                      }}
                    >
                      검증 링크 복사
                    </button>
                    <a className="verify-link-button" href={detail.verificationUri} target="_blank" rel="noreferrer">공개 검증</a>
                  </div>

                  <div className="life-stamp-qr">
                    <div className="life-stamp-qr-code">
                      <QRCodeSVG value={detail.verificationUri} size={132} level="M" />
                    </div>
                    <div>
                      <span>PUBLIC VERIFY</span>
                      <strong>스캔하여 Life Stamp 확인</strong>
                      <p>민감정보를 공개하지 않고 발급기관·성취·현재 유효 상태를 확인합니다.</p>
                    </div>
                  </div>
                </>
              )}

              {notice && <p className="life-stamp-notice">{notice}</p>}
            </>
          ):<WalletEmpty text="Life Stamp를 선택하세요." />}
        </aside>
      </div>
    </section>
  );
}

export function WalletSharePage({client}:{client:PlatformClient}){
  const [items,setItems]=useState<ShareLink[]>([]);
  const [label,setLabel]=useState("나의 진로성장 포트폴리오");
  const [experiences,setExperiences]=useState(true);
  const [growth,setGrowth]=useState(true);
  const [credentials,setCredentials]=useState(true);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);
  const [latest,setLatest]=useState<ShareLink|null>(null);

  useEffect(()=>{
    let active=true;
    client.rpc("wallet_share_links").then(({data,error})=>{
      if(!active) return;
      if(error) setError(error.message);
      else setItems((data || []) as ShareLink[]);
    });
    return()=>{active=false;};
  },[client,refresh]);

  async function create(){
    setBusy(true);
    setError("");
    const {data,error}=await client.rpc("wallet_create_share",{
      p_label:label.trim(),
      p_include_experiences:experiences,
      p_include_growth:growth,
      p_include_credentials:credentials,
      p_valid_days:30
    });
    setBusy(false);

    if(error){
      setError(error.message);
      return;
    }

    const created=data as {shareId:string;shareUrl:string;expiresAt:string};
    setLatest({
      shareId:created.shareId,
      shareUrl:created.shareUrl,
      expiresAt:created.expiresAt,
      label:label.trim(),
      status:"ACTIVE",
      includeExperiences:experiences,
      includeGrowth:growth,
      includeCredentials:credentials,
      createdAt:new Date().toISOString()
    });
    setRefresh((value)=>value+1);
  }

  async function revoke(id:string){
    setError("");
    const {error}=await client.rpc("wallet_revoke_share",{p_share_id:id});
    if(error){
      setError(error.message);
      return;
    }
    if(latest?.shareId===id) setLatest(null);
    setRefresh((value)=>value+1);
  }

  return (
    <section className="wallet-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Privacy-safe Sharing</p>
          <h2>공유</h2>
          <p className="route-description">공개할 범위를 직접 선택하고, 언제든 링크를 취소할 수 있습니다.</p>
        </div>
        <span className="phase-badge">BUILD-05</span>
      </div>

      <div className="wallet-share-builder">
        <label>
          공유 이름
          <input value={label} onChange={(event)=>setLabel(event.target.value)} />
        </label>
        <div className="share-scope-options">
          <label><input type="checkbox" checked={experiences} onChange={(event)=>setExperiences(event.target.checked)} /> 경험</label>
          <label><input type="checkbox" checked={growth} onChange={(event)=>setGrowth(event.target.checked)} /> 성장·성취</label>
          <label><input type="checkbox" checked={credentials} onChange={(event)=>setCredentials(event.target.checked)} /> 자격증명</label>
        </div>
        <button className="primary compact-button" disabled={busy || !label.trim() || !(experiences||growth||credentials)} onClick={create}>
          {busy ? "공유 링크 생성 중…" : "30일 공유 링크 생성"}
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {latest && (
        <div className="wallet-qr-card share-created">
          <QRCodeSVG value={latest.shareUrl} size={160} level="M" />
          <div>
            <p className="eyebrow">새 공유 링크</p>
            <strong>{latest.label}</strong>
            <p>{latest.shareUrl}</p>
            <div className="wallet-button-row">
              <button className="secondary" onClick={()=>copyText(latest.shareUrl)}>링크 복사</button>
              <a className="verify-link-button" href={latest.shareUrl} target="_blank" rel="noreferrer">공개 화면 보기</a>
            </div>
          </div>
        </div>
      )}

      <WalletSection title="공유 링크 관리">
        {items.length ? items.map((item)=>(
          <div className="wallet-list-row" key={item.shareId}>
            <div>
              <strong>{item.label}</strong>
              <span>{item.status==="ACTIVE" ? "공유 중" : item.status==="EXPIRED" ? "만료" : "취소"}</span>
              <small>만료 {formatDate(item.expiresAt)}</small>
            </div>
            <div className="wallet-button-row">
              {item.status==="ACTIVE" && (
                <>
                  <button className="secondary" onClick={()=>copyText(item.shareUrl)}>복사</button>
                  <button className="secondary danger" onClick={()=>revoke(item.shareId)}>공유 취소</button>
                </>
              )}
            </div>
          </div>
        )):<WalletEmpty text="생성한 공유 링크가 없습니다." />}
      </WalletSection>
    </section>
  );
}

export function PublicWalletSharePage({
  client,
  shareId
}:{
  client:PlatformClient|null;
  shareId:string;
}){
  const [data,setData]=useState<PublicShare|null>(null);
  const [portfolio,setPortfolio]=useState<PublicPortfolioShare|null>(null);
  const [error,setError]=useState("");

  useEffect(()=>{
    if(!client) return;
    const rpcClient:PlatformClient=client;
    let active=true;

    async function loadShare(){
      const portfolioResult=await rpcClient.rpc("public_portfolio_share",{p_share_id:shareId});
      if(!active) return;
      if(!portfolioResult.error && portfolioResult.data){
        setPortfolio(portfolioResult.data as PublicPortfolioShare);
        setData(null);
        setError("");
        return;
      }

      const genericResult=await rpcClient.rpc("public_wallet_share",{p_share_id:shareId});
      if(!active) return;
      if(genericResult.error){
        setError("이 공유 링크는 사용할 수 없거나 만료되었습니다.");
        return;
      }
      setData(genericResult.data as PublicShare);
      setPortfolio(null);
      setError("");
    }

    void loadShare();
    return()=>{active=false;};
  },[client,shareId]);

  if(error){
    return (
      <div className="public-share-page">
        <div className="public-share-shell">
          <p className="eyebrow">RS 진로성장 공유</p>
          <h1>공유 링크를 확인할 수 없습니다</h1>
          <p className="muted">{error}</p>
        </div>
      </div>
    );
  }

  if(!data && !portfolio){
    return (
      <div className="public-share-page">
        <div className="public-share-shell"><p className="muted">공유 포트폴리오를 불러오는 중…</p></div>
      </div>
    );
  }

  if(portfolio){
    const meta=portfolio.content?.portfolioMeta || {};
    const sections=(portfolio.content?.sections || []).filter((section)=>section.visible!==false);
    return (
      <div className="portfolio-public-viewer">
        <main className="portfolio-public-shell">
          <div className="portfolio-public-brand">RS LIFE PASSPORT</div>
          <p>{portfolio.profile.displayName} · {portfolioDocumentTypeText(portfolio.documentType)}</p>
          <span className="portfolio-public-purpose">
            {portfolioPurposeText(meta.purpose)}{meta.target ? " · " + meta.target : ""}
          </span>
          <h1>{portfolio.title}</h1>
          {meta.headline && <blockquote className="portfolio-public-headline">{meta.headline}</blockquote>}
          <small>v{portfolio.versionNo} · 공유 만료 {formatDate(portfolio.expiresAt)}</small>

          <div className="portfolio-public-content">
            {sections.map((section)=>(
              <section key={section.key}>
                <h2>{section.title}</h2>
                <p>{section.body}</p>
              </section>
            ))}
          </div>

          {portfolio.experiences.length>0 && (
            <section className="portfolio-public-source-block">
              <h2>선택한 경험</h2>
              {portfolio.experiences.map((item,index)=>(
                <article key={item.activityTitle+"-"+String(index)}>
                  <strong>{item.activityTitle}</strong>
                  <span>{item.programTitle} · {item.trustLabel || "기관 참여 기록"}</span>
                  <small>{formatDate(item.completedAt || item.attendedAt)}</small>
                </article>
              ))}
            </section>
          )}

          {portfolio.lifeStamps.length>0 && (
            <section className="portfolio-public-source-block">
              <h2>Life Stamp</h2>
              {portfolio.lifeStamps.map((item,index)=>(
                <article key={item.credentialName+"-"+String(index)}>
                  <strong>{item.credentialName}</strong>
                  <span>{item.achievementName} · {item.issuerName}</span>
                  <small>{item.trustLabel || (item.isValid ? "기관 발급 · 현재 유효" : "기관 발급 기록")}</small>
                  {item.verificationUri && <a href={item.verificationUri} target="_blank" rel="noreferrer">자격증명 검증</a>}
                </article>
              ))}
            </section>
          )}
        </main>
      </div>
    );
  }

  if(!data) return null;

  return (
    <div className="public-share-page">
      <main className="public-share-shell">
        <p className="eyebrow">RS 진로성장 공유</p>
        <h1>{data.profile.displayName}</h1>
        <p className="muted">{data.profile.schoolName || ""} · {data.label}</p>
        <small>공유 만료 {formatDate(data.expiresAt)}</small>

        {data.experiences.length>0 && (
          <WalletSection title="경험">
            {data.experiences.map((item,index)=>(
              <div className="wallet-list-row" key={`${item.activityTitle}-${index}`}>
                <div>
                  <strong>{item.activityTitle}</strong>
                  <span>{item.programTitle}</span>
                  <small>{formatDate(item.completedAt || item.attendedAt)}</small>
                </div>
              </div>
            ))}
          </WalletSection>
        )}

        {(data.growth.competencies.length>0 || data.growth.achievements.length>0) && (
          <div className="wallet-two-column">
            <WalletSection title="역량">
              {data.growth.competencies.map((item,index)=>(
                <div className="wallet-list-row" key={`${item.name}-${index}`}>
                  <div><strong>{item.name}</strong><small>{formatDate(item.observedAt)}</small></div>
                  <span className="wallet-score">{item.score}</span>
                </div>
              ))}
            </WalletSection>
            <WalletSection title="성취">
              {data.growth.achievements.map((item,index)=>(
                <div className="wallet-list-row" key={`${item.name}-${index}`}>
                  <div><strong>{item.name}</strong><span>{item.description || item.periodKey}</span></div>
                  <span className="status-pill good">확인</span>
                </div>
              ))}
            </WalletSection>
          </div>
        )}

        {data.credentials.length>0 && (
          <WalletSection title="디지털 자격증명">
            {data.credentials.map((item,index)=>(
              <div className="wallet-list-row" key={`${item.credentialName}-${index}`}>
                <div>
                  <strong>{item.credentialName}</strong>
                  <span>{item.achievementName}</span>
                  <small>{item.issuerName} · {formatDate(item.issuedAt)}</small>
                </div>
                <div className="wallet-button-row">
                  <span className={item.isValid ? "status-pill good" : "status-pill"}>
                    {statusText(item.status,item.isValid,item.expiresAt)}
                  </span>
                  {item.verificationUri && <a className="verify-link-button" href={item.verificationUri} target="_blank" rel="noreferrer">검증</a>}
                </div>
              </div>
            ))}
          </WalletSection>
        )}
      </main>
    </div>
  );
}

function WalletMetric({label,value}:{label:string;value:number}){
  return <div className="wallet-metric"><span>{label}</span><strong>{value}</strong></div>;
}

function WalletSection({title,children}:{title:string;children:React.ReactNode}){
  return (
    <section className="wallet-section">
      <h3>{title}</h3>
      <div className="wallet-section-body">{children}</div>
    </section>
  );
}

function WalletEmpty({text}:{text:string}){
  return <p className="case-empty">{text}</p>;
}
